
import { useStore } from "@/store/useStore";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuthContext } from "@/contexts/auth";
import { isUserFullyAuthenticated, logAuthState, isValidUserId } from "@/utils/authValidation";

export function useDealerFavorite(dealerId: string) {
  const { toggleFavoriteDealer, favoritesDealers, fetchFavoriteDealers } = useStore();
  const { user, isAuthenticated } = useAuthContext();
  const [isFavorite, setIsFavorite] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  
  // Verify authentication status using utility function
  const isUserFullyAuth = isUserFullyAuthenticated(isAuthenticated, user);
  
  // Initialize favorite state
  useEffect(() => {
    // Check if the dealer is in the favoritesDealers array
    const isCurrentlyFavorite = favoritesDealers.includes(dealerId);
    setIsFavorite(isCurrentlyFavorite);
  }, [favoritesDealers, dealerId]);
  
  // Fetch latest favorites when authentication changes
  useEffect(() => {
    if (isUserFullyAuth && user?.id) {
      console.log(`Fetching favorites in useDealerFavorite for dealer ${dealerId}`);
      fetchFavoriteDealers();
    }
  }, [isUserFullyAuth, user, fetchFavoriteDealers, dealerId]);
  
  // Detailed logging for debugging
  useEffect(() => {
    logAuthState(isAuthenticated, user, "useDealerFavorite", { 
      dealerId,
      isUserFullyAuth,
      isFavorite,
      favoritesCount: favoritesDealers.length,
      fromHook: "useDealerFavorite"
    });
  }, [isAuthenticated, user, dealerId, isUserFullyAuth, isFavorite, favoritesDealers.length]);
  
  const handleToggleFavorite = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent multiple clicks
    if (isToggling) return;
    
    // Log authentication state
    console.log("Auth state pre-check:", { 
      isAuthenticated, 
      userExists: !!user,
      userId: user?.id,
      dealerId,
      currentlyFavorite: isFavorite
    });
    
    const userId = user?.id;
    
    // Check authentication
    if (!isValidUserId(userId)) {
      console.error("Authentication failed:", { 
        isAuthenticated, 
        user,
        userId,
        authContext: "useDealerFavorite" 
      });
      
      toast.error("Accesso richiesto", {
        description: "Per aggiungere un compro oro ai preferiti, devi prima accedere al tuo account",
        duration: 4000
      });
      return;
    }
    
    try {
      setIsToggling(true);
      console.log("Authentication verified. Toggling favorite for dealer", dealerId, "user:", userId);
      
      // Chiamiamo direttamente la funzione dello store senza aggiornare lo stato locale
      // Lo store si occuperà di aggiornare il database e poi lo stato locale
      await toggleFavoriteDealer(dealerId, userId);
      
      // Non aggiorniamo lo stato locale qui, lo farà lo store
      // Non mostriamo toast qui, lo farà lo store
      
      // Non facciamo fetchFavoriteDealers qui, lo farà lo store se necessario
    } catch (error) {
      console.error("Error toggling favorite:", error);
      
      // Revert the optimistic update if there was an error
      setIsFavorite(isFavorite);
      
      // Check if it's a duplicate key error (which we can ignore as it means
      // the favorite status is already what the user wanted)
      const errorMessage = (error as any)?.message || '';
      const isDuplicateError = errorMessage.includes('duplicate key') || 
                              (error as any)?.code === '23505';
      
      if (!isDuplicateError) {
        toast.error("Errore", {
          description: "Si è verificato un errore durante la gestione dei preferiti",
          duration: 3000
        });
      }
    } finally {
      setIsToggling(false);
    }
  }, [isToggling, isAuthenticated, user, dealerId, isFavorite, toggleFavoriteDealer, fetchFavoriteDealers]);
  
  return {
    handleToggleFavorite,
    isAuthenticated: isUserFullyAuth,
    isFavorite,
    isToggling
  };
}
